/* eslint-disable no-restricted-syntax */
import { useQuery } from '@apollo/client';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import {
  HelpPageBookingsDocument,
  HelpPageBookingsQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Row } from 'react-bootstrap';
import HelpButton from '../../HelpButton';
import CannotFindBooking from './CannotFindBooking';
import IndividualBooking from './individualBooking/IndividualBooking';

/**
 * Help booking type, including if the user `isOwner` or is a guest.
 */
export type HelpBooking =
  HelpPageBookingsQuery['user']['bookings']['items'][0] & { isOwner: boolean };

/**
 * Sort bookings by date
 *
 * For use with `Array.sort()`
 */
export function sortBookingsByDate(a: HelpBooking, b: HelpBooking) {
  const aDate = DateTime.fromISO(a.group.start.utc);
  const bDate = DateTime.fromISO(b.group.start.utc);
  if (bDate > aDate) return -1;
  if (aDate > bDate) return 1;
  return 0;
}

/**
 * Combine the owned bookings and bookings that the user is a guest on
 */
export function combineBookingsGuestOwner(
  data: HelpPageBookingsQuery,
): HelpBooking[] {
  const bookings: HelpBooking[] = data.user.bookings.items.map((booking) => ({
    ...booking,
    isOwner: true,
  }));

  for (const guest of data.user.guestOnBookings.items) {
    const isOwner = bookings.find((booking) => booking.id === guest.booking.id);

    if (!isOwner) {
      const booking = { ...guest.booking, isOwner: false };
      bookings.push(booking);
    }
  }

  const sortedBookings = bookings.sort(sortBookingsByDate);

  return sortedBookings;
}

/**
 * Select Booking Section
 */
export default function SelectBooking({ userID }: { userID: string }) {
  const [currentActiveKey, setActiveKey] = useState<string>();
  const buttonProps = { currentActiveKey, setActiveKey };

  const { loading, data, error } = useQuery(HelpPageBookingsDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      id: userID,
    },
  });

  if (loading && !data) {
    return <LoadingPage />;
  }

  if (error && !data) {
    return <ErrorPage error={error} />;
  }

  const bookings = combineBookingsGuestOwner(data);

  // If the user doesn't have any bookings, show them how to find it
  if (bookings.length === 0) {
    return (
      <div className="mt-3">
        <h2 className="h4">You don't have any bookings on this account</h2>

        <CannotFindBooking />
      </div>
    );
  }

  return (
    <div className="mt-3">
      <h2 className="h4">Which booking would you like help with?</h2>
      <Row className="mt-4">
        {bookings.map((booking) => (
          <HelpButton
            {...buttonProps}
            key={booking.id}
            activeKey={booking.id}
            text={`${booking.trip.title}: ${DateTime.fromISO(
              booking.group.start.utc,
              {
                zone: 'utc',
              },
            ).toLocaleString(DateTime.DATE_MED)}`}
          >
            <IndividualBooking userID={userID} booking={booking} />
          </HelpButton>
        ))}

        <HelpButton
          {...buttonProps}
          activeKey="cantFind"
          text="I can't see my booking"
        >
          <div className="mt-4">
            <h2 className="h4">
              It's likely that you're signed in to the wrong account
            </h2>

            <CannotFindBooking />
          </div>
        </HelpButton>
      </Row>
    </div>
  );
}
